/**
 * ArgumentDetailView — Stage view rendering a single Argomento with the
 * 5-section editorial format (inquadramento, argomento passo per passo,
 * punto decisivo, obiezioni storiche, eredità) plus bilingual citations.
 *
 * Inline `{{id|label}}` markers in the editorial text are turned into
 * .term-marker spans by renderTermsParagraphs / renderTermsInline; the
 * page-singleton TermPopover handles hover/tap to open glossary entries.
 *
 * Navigation back: defaults to the author's detail view (the argomento
 * always belongs to one author).
 */

import { BaseComponent } from '@/core/BaseComponent';
import type { ComponentContext } from '@/core/BaseComponent';
import { getDataService } from '@/services/data-registry';
import type { ArgomentoLocalized } from '@/data/schema';
import { renderTermsParagraphs, renderTermsInline } from './terms';
import { mountArgumentMap } from '@/components/ArgumentMapView';

export class ArgumentDetailView extends BaseComponent {
  private readonly argomentoId: string;
  private arg: ArgomentoLocalized | null = null;
  private authorName = '';
  /** click delegato su this.element: attaccare UNA volta (evita clic multipli). */
  private wired = false;
  /** observer sezioni: uno solo, ricreato al re-render disconnettendo il precedente. */
  private sectionObserver: IntersectionObserver | null = null;

  constructor(element: HTMLElement, context: ComponentContext, argomentoId: string) {
    super(element, context);
    this.argomentoId = argomentoId;
  }

  protected async onInit(): Promise<void> {
    this.element.classList.add('argument-detail-view');
    const data = getDataService();
    this.arg = await data.getArgomento(this.argomentoId, this.context.locale);
    if (!this.arg) {
      this.renderNotFound();
      return;
    }
    const author = await data.getAuthor(this.arg.autore_id, this.context.locale);
    this.authorName = author?.nome ?? this.arg.autore_id;

    if (this.isLimMode()) {
      this.renderLim();
    } else {
      this.render(this.arg, this.authorName);
      this.wireEvents();
    }

    // React to display mode changes
    this.addCleanup(
      this.context.bus.on('display:change', () => {
        if (!this.arg) return;
        this.element.innerHTML = '';
        if (this.isLimMode()) {
          this.renderLim();
        } else {
          this.render(this.arg, this.authorName);
          this.wireEvents();
          // NB: render() chiama già setupSectionObserver() → NON richiamarlo qui
          // (creava un secondo observer che confliggeva col primo).
        }
      }),
    );
  }

  private isLimMode(): boolean {
    return document.documentElement.getAttribute('data-display') === 'lim';
  }

  private renderLim(): void {
    const arg = this.arg!;
    mountArgumentMap(this.element, arg.id, this.context.locale as 'it' | 'en', () => {
      this.context.bus.emit('navigate:back', {});
    });
  }

  private renderNotFound(): void {
    this.element.innerHTML = `
      <div class="empty-state">
        <p>Argomento non trovato: <code>${esc(this.argomentoId)}</code></p>
        <button type="button" class="btn" data-action="home">Torna alla lista</button>
      </div>
    `;
    this.addEventListener(this.element, 'click', (ev) => {
      const t = ev.target as HTMLElement;
      if (t.dataset.action === 'home') this.context.bus.emit('navigate:home', {});
    });
  }

  private render(arg: ArgomentoLocalized, authorName: string): void {
    const c = arg.content;
    const passo = c.argomento_passo_passo;

    const premesseHtml = passo.premesse
      .map(
        (p) => `
          <article class="premise" data-fragment-id="argomento:${attr(arg.id)}/premessa/P${attr(String(p.numero))}">
            <header class="premise-head">
              <span class="premise-num">P${p.numero}</span>
              <h3 class="premise-statement">${renderTermsInline(p.enunciato)}</h3>
            </header>
            <div class="premise-comment">${renderTermsParagraphs(p.commento)}</div>
          </article>
        `,
      )
      .join('');

    const obiezioniHtml = c.obiezioni_storiche
      .map((o, idx) => {
        const meta = arg.obiezioni.find((m) => m.id === o.id);
        const annoLabel = o.anno ? ` (${o.anno})` : '';
        const operaLabel = o.opera ? `<p class="objection-source">${esc(o.opera)}</p>` : '';
        const rifLabel = meta?.riferimento
          ? `<p class="objection-ref">${esc([meta.riferimento.titolo, meta.riferimento.edizione, meta.riferimento.passaggio].filter(Boolean).join(', '))}</p>`
          : '';
        const risposta = o.risposta
          ? `
            <details class="objection-reply">
              <summary>Replica di ${esc(authorName)}</summary>
              <div class="objection-reply-body">${renderTermsParagraphs(o.risposta)}</div>
            </details>
          `
          : '';
        return `
          <article class="objection" data-fragment-id="argomento:${attr(arg.id)}/obiezione/${attr(o.id)}">
            <header class="objection-head">
              <span class="objection-num">${idx + 1}</span>
              <div class="objection-meta">
                <h3 class="objection-author">${esc(o.autore_nome)}${esc(annoLabel)}</h3>
                ${operaLabel}
                ${rifLabel}
              </div>
            </header>
            <div class="objection-body">${renderTermsParagraphs(o.obiezione)}</div>
            ${risposta}
          </article>
        `;
      })
      .join('');

    const citazioniHtml = c.citazioni
      .map((cit) => {
        const meta = arg.citazioni.find((m) => m.id === cit.id);
        const rif = meta?.riferimento;
        const rifLabel = rif
          ? [rif.titolo, rif.edizione, rif.passaggio].filter(Boolean).join(', ')
          : '';
        const lang = meta?.lingua_originale ?? 'la';
        return `
          <figure class="argument-citation" data-fragment-id="argomento:${attr(arg.id)}/citazione/${attr(cit.id)}">
            <blockquote class="argument-citation-original" lang="${attr(lang)}">${esc(cit.testo_originale)}</blockquote>
            <blockquote class="argument-citation-translation">${esc(cit.testo_tradotto)}</blockquote>
            ${rifLabel ? `<figcaption class="argument-citation-source">— ${esc(rifLabel)}</figcaption>` : ''}
          </figure>
        `;
      })
      .join('');

    const opera = arg.opera_titolo ? `<p class="argument-opera">${esc(arg.opera_titolo)}</p>` : '';
    const introHtml = passo.introduzione ? renderTermsParagraphs(passo.introduzione) : '';

    this.element.innerHTML = `
      <nav class="argument-sidenav">
        <button type="button" class="btn btn-back argument-sidenav-back" data-action="back">← Indietro</button>
        <div class="argument-sidenav-links">
          <a class="argument-sidenav-link" data-section="sec-inquadramento">1. Inquadramento</a>
          <a class="argument-sidenav-link" data-section="sec-passo-passo">2. Passo per passo</a>
          <a class="argument-sidenav-link" data-section="sec-decisivo">3. Punto decisivo</a>
          <a class="argument-sidenav-link" data-section="sec-obiezioni">4. Obiezioni</a>
          <a class="argument-sidenav-link" data-section="sec-eredita">5. Eredità</a>
          ${citazioniHtml ? '<a class="argument-sidenav-link" data-section="sec-citazioni">Citazioni</a>' : ''}
        </div>
      </nav>

      <div class="argument-content">
        <header class="argument-header">
          <p class="argument-eyebrow">Argomento</p>
          <h1 class="argument-title" data-fragment-id="argomento:${attr(arg.id)}/titolo">${esc(arg.nome)}</h1>
          <p class="argument-author">${esc(authorName)}</p>
          ${opera}
          <p class="argument-statement" data-fragment-id="argomento:${attr(arg.id)}/enunciato">${renderTermsInline(c.enunciato)}</p>
        </header>

        <section id="sec-inquadramento" class="argument-section argument-section-inquadramento">
          <h2 data-fragment-id="argomento:${attr(arg.id)}/inquadramento-titolo">Inquadramento</h2>
          <div class="argument-prose" data-fragment-id="argomento:${attr(arg.id)}/inquadramento">${renderTermsParagraphs(c.inquadramento)}</div>
        </section>

        <section id="sec-passo-passo" class="argument-section argument-collapsible" data-collapsed="true">
          <h2 class="argument-section-toggle" data-fragment-id="argomento:${attr(arg.id)}/passo-titolo">L'argomento passo per passo <span class="toggle-chevron">›</span></h2>
          <div class="argument-section-body">
            ${introHtml ? `<div class="argument-prose argument-passo-intro">${introHtml}</div>` : ''}
            <div class="premises">${premesseHtml}</div>
            <div class="conclusion">
              <header class="conclusion-head"><span class="conclusion-arrow">∴</span><span class="conclusion-label">Conclusione</span></header>
              <div class="argument-prose" data-fragment-id="argomento:${attr(arg.id)}/conclusione">${renderTermsParagraphs(passo.conclusione)}</div>
            </div>
          </div>
        </section>

        <section id="sec-decisivo" class="argument-section argument-collapsible" data-collapsed="true">
          <h2 class="argument-section-toggle" data-fragment-id="argomento:${attr(arg.id)}/decisivo-titolo">Il punto filosofico decisivo <span class="toggle-chevron">›</span></h2>
          <div class="argument-section-body">
            <div class="argument-prose argument-decisivo" data-fragment-id="argomento:${attr(arg.id)}/punto-decisivo">${renderTermsParagraphs(c.punto_decisivo)}</div>
          </div>
        </section>

        <section id="sec-obiezioni" class="argument-section argument-collapsible" data-collapsed="true">
          <h2 class="argument-section-toggle" data-fragment-id="argomento:${attr(arg.id)}/obiezioni-titolo">Obiezioni storiche <span class="toggle-chevron">›</span></h2>
          <div class="argument-section-body">
            <div class="objections">${obiezioniHtml}</div>
          </div>
        </section>

        <section id="sec-eredita" class="argument-section argument-collapsible" data-collapsed="true">
          <h2 class="argument-section-toggle" data-fragment-id="argomento:${attr(arg.id)}/eredita-titolo">Eredità <span class="toggle-chevron">›</span></h2>
          <div class="argument-section-body">
            <div class="argument-prose" data-fragment-id="argomento:${attr(arg.id)}/eredita">${renderTermsParagraphs(c.eredita)}</div>
          </div>
        </section>

        ${citazioniHtml ? `
          <section id="sec-citazioni" class="argument-section argument-collapsible argument-citations-section" data-collapsed="true">
            <h2 class="argument-section-toggle" data-fragment-id="argomento:${attr(arg.id)}/citazioni-titolo">Citazioni <span class="toggle-chevron">›</span></h2>
            <div class="argument-section-body">
              <div class="argument-citations">${citazioniHtml}</div>
            </div>
          </section>
        ` : ''}
      </div>
    `;

    this.setupSectionObserver();
  }

  /**
   * IntersectionObserver to highlight the active section in the sidenav.
   * The scroll container is the nearest scrollable ancestor (stage-router-container).
   */
  private setupSectionObserver(): void {
    // Disconnetti l'observer precedente prima di crearne uno nuovo (il DOM delle
    // sezioni viene ricostruito ad ogni render → altrimenti si accumulano).
    this.sectionObserver?.disconnect();
    this.sectionObserver = null;

    const sections = this.element.querySelectorAll<HTMLElement>('.argument-section');
    const links = this.element.querySelectorAll<HTMLElement>('.argument-sidenav-link');
    if (sections.length === 0 || links.length === 0) return;

    // Find the scroll root — walk up until we find an overflow container
    let scrollRoot: HTMLElement | null = this.element.parentElement;
    while (scrollRoot && getComputedStyle(scrollRoot).overflowY !== 'auto' && getComputedStyle(scrollRoot).overflowY !== 'scroll') {
      scrollRoot = scrollRoot.parentElement;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = entry.target.id;
          for (const link of links) {
            link.classList.toggle(
              'argument-sidenav-link-active',
              link.dataset.section === id,
            );
          }
        }
      },
      {
        root: scrollRoot,
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0,
      },
    );

    for (const section of sections) observer.observe(section);
    this.sectionObserver = observer;
    this.addCleanup(() => observer.disconnect());
  }

  private wireEvents(): void {
    if (this.wired) return;
    this.wired = true;
    this.addEventListener(this.element, 'click', (ev) => {
      const t = ev.target as HTMLElement;
      if (t.closest<HTMLElement>('[data-action="back"]')) {
        this.context.bus.emit('navigate:back', {});
        return;
      }
      // Collapsible section toggle
      const toggle = t.closest<HTMLElement>('.argument-section-toggle');
      if (toggle) {
        const section = toggle.closest<HTMLElement>('.argument-collapsible');
        if (section) {
          const isCollapsed = section.dataset.collapsed === 'true';
          section.dataset.collapsed = isCollapsed ? 'false' : 'true';
        }
        return;
      }
      // Smooth-scroll for sidenav links
      const sideLink = t.closest<HTMLElement>('.argument-sidenav-link');
      if (sideLink?.dataset.section) {
        const target = this.element.querySelector<HTMLElement>(`#${sideLink.dataset.section}`);
        if (target) {
          ev.preventDefault();
          // Auto-expand if collapsed
          if (target.classList.contains('argument-collapsible') && target.dataset.collapsed === 'true') {
            target.dataset.collapsed = 'false';
          }
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });
  }
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function attr(s: string): string {
  return esc(s).replace(/"/g, '&quot;');
}
